import React, { forwardRef, useMemo } from "react";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { EditorRefPlugin } from "@lexical/react/LexicalEditorRefPlugin";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { HorizontalRulePlugin } from "@lexical/react/LexicalHorizontalRulePlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { CheckListPlugin } from "@lexical/react/LexicalCheckListPlugin";
import { TabIndentationPlugin } from "@lexical/react/LexicalTabIndentationPlugin";
import LexicalErrorBoundary from "@lexical/react/LexicalErrorBoundary";
import Toolbar from "../components/Toolbar/Toolbar";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { TablePlugin } from "@lexical/react/LexicalTablePlugin";
import ImagesPlugin from "../components/Image/Image";
import { Flex, MenuProps } from "antd";
import { LexicalEditor } from "lexical";
import { BeautifulMentionsMenuProps, BeautifulMentionsPlugin } from "lexical-beautiful-mentions";
import "../../../../../../style/mentionStyle.css";

const container: React.CSSProperties = {
  width: "100%",
  minHeight: "410px",
  backgroundColor: "white",
  padding: "24px 40px",
  borderBottomLeftRadius: "5px",
  borderBottomRightRadius: "5px",
  boxSizing: "border-box",
  border: "0.5px solid #d9d9d9",
  overflowY: "auto",
  overflowX: "hidden",
};

const contentEditable: React.CSSProperties = {
  height: "64px",
  boxSizing: "border-box",
  resize: "none",
  fontSize: "15px",
  caretColor: "#444",
  position: "relative",
  tabSize: 1,
  outline: 0,
  color: "#000",
  margin: "0px",
};

interface IRichTextEditor {
  editorRef: React.MutableRefObject<LexicalEditor | null | undefined>;
  orgStructureWidgets?: object[];
  definedWidgets?: object[];
  widgetItems?: MenuProps['items'];
  setCurrentSelected?: (selected: string) => void;
}

const CustomMenuItem = forwardRef<
  HTMLLIElement,
  BeautifulMentionsMenuProps
>(({ selected, item, ...props }, ref) => (
  <li
    className={`m-0 flex ${selected ? "bg-gray-100" : "bg-white"}`}
    {...props}
    ref={ref}
    style={{
      listStyleType: "none",
      whiteSpace: "nowrap",
      padding: "4px 8px",
      cursor: "pointer",
      backgroundColor: selected ? "#f5f5f5" : "white",
    }}
  />
));

export const RichTextEditor: React.FC<IRichTextEditor> = ({ widgetItems, editorRef, orgStructureWidgets = [], definedWidgets = [], setCurrentSelected }) => {

  const mentionItems = useMemo(() => {
    const list: string[] = [];
    if (widgetItems && widgetItems.length > 0) {
      widgetItems.forEach((item: any) => {
        if (item.label) {
          list.push(item.label);
        }
      });
    } else {
      orgStructureWidgets.forEach((r: any) => list.push(r.label));
      definedWidgets.forEach((r: any) => list.push(r.label));
    }
    return { "@": list };
  }, [widgetItems, orgStructureWidgets, definedWidgets]);

  return (
    <Flex vertical={true}>
      <Toolbar editable={true} widgetItems={widgetItems} />
      <Flex
        style={container}
        onClick={() => {
          if (setCurrentSelected) {
            setCurrentSelected('EDITOR');
          }
        }}
      >
        <RichTextPlugin
          contentEditable={
            <ContentEditable
              style={contentEditable}
              onFocus={() => {
                if (setCurrentSelected) {
                  setCurrentSelected('EDITOR');
                }
              }}
            />
          }
          placeholder={null}
          ErrorBoundary={LexicalErrorBoundary}
        />
        <EditorRefPlugin editorRef={editorRef} />
        <HistoryPlugin />
        <ImagesPlugin />
        <ListPlugin />
        <LinkPlugin />
        <TabIndentationPlugin />
        <HorizontalRulePlugin />
        <BeautifulMentionsPlugin
          items={mentionItems}
          menuComponent={() => null}
        />
        <CheckListPlugin />
        <TablePlugin />
      </Flex>
    </Flex>
  );
};

export default RichTextEditor;
